/**
 * Has the tenant behind this token already been migrated off Xano? If so, the link to follow
 * instead of staying in this app.
 *
 * The decision is made server-side by `/api/migration/handoff`, which re-derives the identity
 * from the token by asking Xano rather than trusting anything sent from here.
 *
 * Every failure answers null and the caller stays in this app. A tenant that is not migrated, a
 * new system that is down, a deployment where the two server variables are unset — none of them
 * may cost someone the login they just completed.
 */

export interface HandoffDecision {
  url: string | null;
  reason: 'migrated' | 'not-migrated' | 'unavailable';
}

/**
 * The Xano token this browser is holding, wherever it came from. The cookie is the ordinary
 * case; the URL parameter is the embedded flow, which reaches the inbox with no Redux auth at
 * all and would otherwise skip the check entirely.
 *
 * Splits the cookie on the first "=" only: the name is the part before it, everything after —
 * "=" included — is the value. A token that itself contains "=" would be truncated by a plain
 * `split('=')[1]`.
 */
export function readHandoffToken(): string | null {
  const esChat = document.cookie
    .split(';')
    .find((c) => c.trim().startsWith('es_chat='));
  if (esChat) return esChat.trim().slice('es_chat='.length);
  return new URLSearchParams(window.location.search).get('authToken');
}

// 5s is the right order of magnitude for a check that sits in front of a sign-in: long enough
// for a normal round trip, short enough that a hung new-system endpoint cannot strand every
// visitor to the legacy app behind an endless spinner.
const HANDOFF_TIMEOUT_MS = 5000;

export async function migrationHandoff(authToken: string): Promise<HandoffDecision> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), HANDOFF_TIMEOUT_MS);
  try {
    const res = await fetch('/api/migration/handoff', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token: authToken }),
      signal: controller.signal,
    });
    if (!res.ok) return warn('unavailable', `handoff check answered ${res.status}`);

    const { url, unavailable } = await res.json();
    if (typeof url === 'string' && url) return { url, reason: 'migrated' };
    if (unavailable) return warn('unavailable', 'the new system did not answer the handoff check');
    return { url: null, reason: 'not-migrated' };
  } catch (err) {
    if (err instanceof Error && err.name === 'AbortError') {
      return warn('unavailable', 'the handoff check timed out');
    }
    return warn('unavailable', 'the handoff check could not be reached');
  } finally {
    clearTimeout(timeoutId);
  }
}

/**
 * Returns true when a redirect has been started, so the caller stops rather than carrying on
 * into an app the person is about to leave.
 *
 * `replace`, not `assign`: the back button must not bounce someone into the legacy app they
 * were just moved out of. The redirect is a parameter so tests never need a DOM to drive it.
 */
export async function redirectIfMigrated(
  authToken: string,
  redirect: (url: string) => void = (url) => window.location.replace(url),
): Promise<boolean> {
  const { url, reason } = await migrationHandoff(authToken);
  if (reason === 'migrated' && url) {
    // `Location.replace` throws a SyntaxError on a genuinely malformed URL. That must not
    // propagate as a rejection: this function's contract is that it never rejects, and a caller
    // relying on that (App.tsx's `.then()`, with no `.catch()`) would otherwise be stranded on
    // its loading state forever.
    try {
      redirect(url);
    } catch {
      warn('unavailable', 'the redirect to the new system could not be started');
      return false;
    }
    return true;
  }
  return false;
}

// Fail open, but never fail silent. Without this a rotated secret, a wrong base URL and a
// genuinely unmigrated tenant all look identical, and the symptom of the first two is that
// migrated tenants keep using Xano indefinitely with nobody noticing.
function warn(reason: HandoffDecision['reason'], message: string): HandoffDecision {
  console.warn(`[migration] ${message} — staying in the legacy app`);
  return { url: null, reason };
}
